/**
 * The REST client (web-plan.md F1) — the web port of the desktop's
 * `net/rest.rs`: the same routes against docs/chat-api.md, the same timeout
 * profiles, the same error taxonomy. Where the two runtimes genuinely differ
 * the divergence is written at the site:
 *
 * - the desktop keeps four connection pools and rebuilds them on any network
 *   failure (`reset_connections`); a browser owns its pool and offers no
 *   handle, so that method is a documented no-op here;
 * - the desktop can tell "never connected" from "connected and silent"
 *   (reqwest's `is_connect`); `fetch` cannot, so the two collapse into one
 *   transport error naming the base;
 * - the desktop has **no** 413 handling (oversize is caught before a byte
 *   moves); the web plan (F1) asks the composer to tell 400-a-field from
 *   413-the-whole apart, so `ApiError.tooLarge` exists here on purpose.
 *
 * Same-origin by construction (D46): paths are relative, the dev server and
 * production Caddy both proxy `/v1/*`, and CORS never exists.
 */
import type {
  BansResponse,
  BootstrapResponse,
  BroadcastsResponse,
  ChannelCard,
  ChannelSettings,
  ChannelSummary,
  ChatCommand,
  ChatRowsResponse,
  DirectoryEntry,
  EditBody,
  Friendship,
  Invite,
  Joined,
  LatestResponse,
  LinkPreview,
  MePatch,
  MembersResponse,
  Message,
  MessageReads,
  NewBroadcast,
  PageResponse,
  PetCard,
  PetCommand,
  PetCreated,
  PetDm,
  PetDraft,
  PetPatch,
  PetToken,
  PostOptionsBody,
  ViewsResponse,
  ReactionsAnswer,
  NewSticker,
  SendBody,
  Sticker,
  StickerCatalog,
  StickerPack,
  StickerPackList,
  StickerPatch,
  StickerRecents,
  StickerSuggestions,
  UserJson,
} from "../../gen/dto";

import { session } from "../session";
import { thisBrowser } from "../device";
import { assertCurrent, currentLifetime } from "../lifetime";
import { type Dict, type Lang, dict } from "../i18n";
import { version } from "../../../package.json";

/** Which client is asking (analytics-plan.md D451): `X-Echo-Client` on every
 * request, so the gateway can tell a message, a reaction or a read made from
 * the web from one made from the desktop. The version is package.json's, the
 * one the voice hello names too. A socket carries no header, so a call made
 * from the web is not told apart. Shared with uploads.ts. */
export const CLIENT_HEADER = "X-Echo-Client";
export const CLIENT_NAME = `web/${version}`;

/** The language the window is in (D339): sent as `Accept-Language` so the
 * server's refusals come back in it as `message`, and used for the few
 * sentences this client says about a request itself. Not the browser's own
 * header — that is the browser's language, and the window may be in another. */
let requestLang: Lang = "en";

export function setRequestLang(lang: Lang): void {
  requestLang = lang;
}

/** `Accept-Language` for a request — shared with uploads.ts. */
export function acceptLanguage(): string {
  return requestLang;
}

/** This client's own error wording, in the window's language. */
export function errorWords(): Dict {
  return dict(requestLang);
}

/** The sentence out of a server error body: `message` (the refusal in the
 * language asked for) from a server new enough to write it, `error` (its
 * English) otherwise. */
export function serverSentence(parsed: { error?: unknown; message?: unknown }): string | null {
  if (typeof parsed.message === "string") return parsed.message;
  if (typeof parsed.error === "string") return parsed.error;
  return null;
}

/** Which of the gateway's lanes a path counts against (docs/admission.md D1071).
 * The gateway and the desktop read `echo_wire::admission::lane_of`; this is its
 * one hand-kept copy, and a slip here only holds back the wrong lane for seconds. */
export type Lane = "requests" | "sign_ins" | "transfers";

export function laneOf(path: string): Lane | null {
  const route = path.split("?", 1)[0] ?? path;
  if (["/v1/ws", "/v1/chat/ws", "/healthz", "/readyz", "/metrics"].includes(route)) return null;
  if (route === "/v1/auth/logout") return "requests";
  if (
    route.startsWith("/v1/uploads") ||
    route.startsWith("/v1/avatars/") ||
    route.startsWith("/v1/stickers/") ||
    route.startsWith("/v1/link-preview") ||
    route === "/v1/pet/updates" ||
    // A report, and a sticker's file (docs/fast-start.md D1087): bodies too large
    // for the requests lane, which they would hold back when refused.
    route === "/v1/feedback" ||
    (route.startsWith("/v1/sticker-packs/") && route.endsWith("/stickers")) ||
    (route.startsWith("/v1/channels/") && route.includes("/files/"))
  ) {
    return "transfers";
  }
  return route.startsWith("/v1/auth/") ? "sign_ins" : "requests";
}

/** The longest a `Retry-After` is obeyed: ours says five seconds, and a proxy in
 * front of it, or a typo in a config, does not get to park a page for a day. */
const BUSY_MAX_WAIT_S = 60;

/** What the gateway said when it had too much in work, lane by lane
 * (docs/admission.md D1075): a 503 with a `Retry-After`. Until the time it asked
 * for has passed, nothing of that lane leaves this page: the request is refused
 * here in the gateway's own words, so whatever a caller does with a refusal stays
 * what it was, and every loop and fan-out asks at most once per wait. A 503 with
 * no `Retry-After` is about the thing asked for and stops nothing else. */
const busy = new Map<Lane, { until: number; sentence: string }>();

export function busySentence(path: string): string | null {
  const lane = laneOf(path);
  const held = lane === null ? undefined : busy.get(lane);
  return held !== undefined && Date.now() < held.until ? held.sentence : null;
}

/** How long, in milliseconds, the gateway asked this page to hold back `lane`. */
export function busyLeftMs(lane: Lane): number {
  return Math.max(0, (busy.get(lane)?.until ?? 0) - Date.now());
}

/** Remembers a 503 that asks to wait, spread by up to half as much again so that
 * everybody turned away together does not return together — the chat socket's
 * rule on the desktop. Shared with uploads.ts. A whole millisecond: a timer set for
 * what is left rounds it down, and would wake inside the hold, where `left` reads
 * a fraction or nothing and whoever waited has nothing to go by. */
export function noteBusy(path: string, response: Response, sentence: string): void {
  if (response.status !== 503) return;
  const lane = laneOf(path);
  const header = response.headers.get("Retry-After");
  const seconds = header === null || header.trim() === "" ? Number.NaN : Number(header);
  if (lane === null || !Number.isFinite(seconds) || seconds < 0) return;
  const after = Math.min(seconds, BUSY_MAX_WAIT_S) * 1000;
  busy.set(lane, { until: Math.ceil(Date.now() + after + Math.random() * (after / 2)), sentence });
}

/** Forgets every wait: a test's clean slate. */
export function forgetBusy(): void {
  busy.clear();
}

/** `ask`, and again after each wait the gateway names (D1075), `patience` times
 * at most. A 503 with no wait in it is the caller's: there is nothing to go by,
 * and asking again at once would be the storm this is here to prevent. */
export async function patiently<T>(
  lane: Lane,
  patience: number,
  ask: () => Promise<T>,
): Promise<T> {
  for (let waited = 0; ; waited += 1) {
    try {
      return await ask();
    } catch (e) {
      const left = busyLeftMs(lane);
      if (!(e instanceof ApiError) || e.status !== 503 || left === 0 || waited >= patience) throw e;
      await new Promise((resolve) => setTimeout(resolve, left));
    }
  }
}

/** The three request deadlines (the desktop's four minus the pool split):
 * control for every ordinary JSON route, auth for the two argon2id doors,
 * bulk for a 1 MiB upload chunk on a bad link. */
export const CONTROL_TIMEOUT_MS = 10_000;
export const AUTH_TIMEOUT_MS = 30_000;
export const BULK_TIMEOUT_MS = 60_000;

/** What a signup refusal the client can act on looks like (the rest keep the
 * server's own sentence). */
export type SignupProblem =
  | "name_taken"
  | "email_taken"
  | "too_many_attempts"
  /** The six digits were wrong, or the five minutes ran out. One problem for
   * both, because the server answers one — telling them apart would tell
   * somebody holding a stolen ticket which half of it is still good. */
  | "bad_code"
  /** The ticket is dead: five wrong guesses, and the flow starts again from
   * the address. */
  | "code_burnt"
  /** This server has no relay configured (501). Distinct from the 404 of a
   * server too old to know the route, which is `unsupported`: registration
   * falls back to one step from either, but only this one can be told to a
   * person asking to recover a password. */
  | "no_mailer"
  /** The route is not there at all: a gateway older than confirmation. */
  | "unsupported"
  /** D312: the proof the old mailbox bought ran out (ten minutes) or was
   * spent — the address change starts again from the first code. */
  | "change_expired";
/** Same for the profile routes; `wrong_current_password` is the one 401 that
 * must never reach the "401 ⇒ logout" path (R50). */
export type ProfileProblem = "username_taken" | "wrong_current_password";

export class ApiError extends Error {
  /** HTTP status, or 0 for a transport failure (offline, DNS, timeout). */
  readonly status: number;
  /** The server's sentence when it wrote one — its `message` in the window's
   * language (D339), or its English `error` from a server older than that. */
  readonly serverText: string | null;
  /** Which field a refusal pointed at, when it did. */
  readonly field: string | null;
  readonly signup: SignupProblem | null;
  readonly profile: ProfileProblem | null;

  constructor(opts: {
    status: number;
    serverText?: string | null;
    field?: string | null;
    signup?: SignupProblem | null;
    profile?: ProfileProblem | null;
    fallback: string;
  }) {
    super(opts.serverText ?? opts.fallback);
    this.status = opts.status;
    this.serverText = opts.serverText ?? null;
    this.field = opts.field ?? null;
    this.signup = opts.signup ?? null;
    this.profile = opts.profile ?? null;
  }

  /** The shell logs out on this — except during the password-change window
   * (R44) and except when `profile === "wrong_current_password"` (R50). */
  get unauthorized(): boolean {
    return this.status === 401 && this.profile === null;
  }

  get notFound(): boolean {
    return this.status === 404;
  }

  get rateLimited(): boolean {
    return this.status === 429;
  }

  /** 413 is "the message as a whole is too large" and is cured only by
   * splitting; a 400 points at a field (chat-api.md §4). */
  get tooLarge(): boolean {
    return this.status === 413;
  }

  get isTransport(): boolean {
    return this.status === 0;
  }
}

/** The desktop rebuilds its connection pools here; a browser has no handle on
 * its own. The call sites survive (the reconnect sequencing around them is
 * load-bearing), the body is honest about doing nothing. */
export function resetConnections(): void {}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
let unauthorizedOwner = 0;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): () => void {
  const owner = ++unauthorizedOwner;
  onUnauthorized = handler;
  return () => {
    if (unauthorizedOwner === owner) onUnauthorized = null;
  };
}

interface Call {
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  body?: unknown;
  timeoutMs?: number;
  /** login/register send no bearer. */
  bare?: boolean;
  /** A read nobody is waiting on, marked for the gateway's background lane
   * (`Echo-Fetch: background`, offline-first-plan.md D321): it paces these with
   * a 429, and leaves everything unmarked alone. */
  background?: boolean;
  /** Map a refusal into a typed problem before the generic path sees it. */
  classify?: (status: number, text: string | null, field: string | null) => ApiError | null;
}

function deadlineFor(path: string): number {
  return path.startsWith("/v1/auth/") ? AUTH_TIMEOUT_MS : CONTROL_TIMEOUT_MS;
}

async function call<T>(spec: Call): Promise<T> {
  const lifetime = currentLifetime();
  const headers: Record<string, string> = {
    "Accept-Language": acceptLanguage(),
    [CLIENT_HEADER]: CLIENT_NAME,
  };
  if (spec.body !== undefined) headers["Content-Type"] = "application/json";
  if (spec.background === true) headers["Echo-Fetch"] = "background";
  if (!spec.bare) {
    const s = session();
    if (!s) {
      throw new ApiError({ status: 0, fallback: errorWords().errorNotLoggedIn() });
    }
    headers["Authorization"] = `Bearer ${s.jwt}`;
  }

  const held = busySentence(spec.path);
  if (held !== null) {
    throw (
      spec.classify?.(503, held, null) ??
      new ApiError({ status: 503, serverText: held, fallback: held })
    );
  }

  let response: Response;
  try {
    response = await fetch(spec.path, {
      method: spec.method,
      headers,
      body: spec.body === undefined ? undefined : JSON.stringify(spec.body),
      signal: AbortSignal.any([
        lifetime.signal,
        AbortSignal.timeout(spec.timeoutMs ?? deadlineFor(spec.path)),
      ]),
    });
  } catch (e) {
    assertCurrent(lifetime);
    resetConnections();
    throw new ApiError({ status: 0, fallback: describeTransport(e) });
  }

  assertCurrent(lifetime);

  // The success band is 200..300 — a 3xx is a failure, not a redirect to
  // follow into the weeds.
  if (response.status < 200 || response.status >= 300) {
    const text = await response.text().catch(() => "");
    assertCurrent(lifetime);
    let serverText: string | null = null;
    let field: string | null = null;
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string; field?: string };
      serverText = serverSentence(parsed);
      field = typeof parsed.field === "string" ? parsed.field : null;
    } catch {
      // A proxy's HTML 502 becomes "HTTP 502", never the HTML.
    }
    const classified = spec.classify?.(response.status, serverText, field) ?? null;
    const error =
      classified ??
      new ApiError({
        status: response.status,
        serverText,
        field,
        fallback: errorWords().errorHttp(response.status),
      });
    noteBusy(spec.path, response, error.message);
    if (error.unauthorized && !spec.bare) onUnauthorized?.();
    throw error;
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text().catch(() => "");
  assertCurrent(lifetime);
  if (text === "") return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // The person needs to know it was the answer, not their input.
    throw new ApiError({ status: 0, fallback: errorWords().errorBadAnswer() });
  }
}

/** A request that got no answer, worded for the person. The browser's own
 * sentence ("Failed to fetch") names nothing they can act on and is in the
 * browser's language, not the window's; the network panel keeps the detail. */
export function describeTransport(e: unknown): string {
  if (e instanceof DOMException && e.name === "TimeoutError") {
    return errorWords().errorTimeout();
  }
  return errorWords().errorNetwork();
}

// ---------------------------------------------------------------------------
// Auth. `login` answers `{jwt}` and nothing else — identity comes from a
// best-effort `GET /v1/me` right after, so a gateway too old to know the
// route still logs in (the desktop's exact choreography).

export function login(username: string, password: string): Promise<{ jwt: string }> {
  return call({
    method: "POST",
    path: "/v1/auth/login",
    // What this browser is, for its line on the account's list of sessions
    // (docs/signins.md D502).
    body: { username, password, device: thisBrowser() },
    bare: true,
  });
}

/** `POST /v1/auth/logout` (docs/signins.md D497): ends this browser's
 * sign-in on the gateway, which since D502 is a line on the account's list
 * of sessions like any other. Sent on the token as it stands and on no
 * session lifetime — the sign-out that sends it ends that lifetime a moment
 * later — `keepalive`, so a tab closed right after still sends it, and best
 * effort: the session is forgotten here whatever the gateway answers. */
export function logout(): void {
  const s = session();
  if (!s) return;
  void fetch("/v1/auth/logout", {
    method: "POST",
    headers: {
      "Accept-Language": acceptLanguage(),
      [CLIENT_HEADER]: CLIENT_NAME,
      Authorization: `Bearer ${s.jwt}`,
    },
    keepalive: true,
  }).catch(() => {});
}

export function register(
  username: string,
  email: string,
  password: string,
): Promise<{ user_id: number }> {
  return call({
    method: "POST",
    path: "/v1/auth/register",
    body: { username, email, password },
    bare: true,
    classify: (status, text, field) => {
      if (status === 409 && field === "username")
        return new ApiError({
          status,
          serverText: text,
          field,
          signup: "name_taken",
          fallback: "name taken",
        });
      if (status === 409 && field === "email")
        return new ApiError({
          status,
          serverText: text,
          field,
          signup: "email_taken",
          fallback: "email taken",
        });
      if (status === 429)
        return new ApiError({
          status,
          serverText: text,
          field,
          signup: "too_many_attempts",
          fallback: "too many attempts",
        });
      return null;
    },
  });
}

// ---------------------------------------------------------------------------
// Confirming an address (gateway/src/confirm.rs). Six digits, five minutes,
// and the same code back for as long as it lives.

/** What every step that puts a letter in the post answers with. */
export interface CodeSent {
  ticket: string;
  expires_in_s: number;
  /** Seconds before «send it again» will send anything. */
  resend_in_s: number;
}

/** A 404 is a gateway older than the whole flow; a 501 is one that could
 * confirm but was never given a relay. Registration falls back to one step on
 * both (a gateway without a relay never requires a code); recovery can only say
 * the server cannot send mail. */
const codeClassify = (
  status: number,
  text: string | null,
  field: string | null,
): ApiError | null => {
  if (status === 404)
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "unsupported",
      fallback: "not supported",
    });
  if (status === 501)
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "no_mailer",
      fallback: "no mailer",
    });
  if (status === 429 && field === "code")
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "code_burnt",
      fallback: "too many wrong codes",
    });
  if (status === 429)
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "too_many_attempts",
      fallback: "too many attempts",
    });
  if (field === "code")
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "bad_code",
      fallback: "wrong code",
    });
  return null;
};

/** `POST /v1/auth/register/start` — validates the whole form, mails a code and
 * creates nothing. The account appears at {@link registerConfirm}. */
export function registerStart(
  username: string,
  email: string,
  password: string,
  lang: string,
): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/auth/register/start",
    body: { username, email, password, lang },
    bare: true,
    classify: codeClassify,
  });
}

/** `POST /v1/auth/register/confirm` — the account exists after this, and the
 * two 409s of one-step registration arrive here instead (the name was free
 * when the letter went out and may not be now). */
export function registerConfirm(ticket: string, code: string): Promise<{ user_id: number }> {
  return call({
    method: "POST",
    path: "/v1/auth/register/confirm",
    body: { ticket, code },
    bare: true,
    classify: (status, text, field) => {
      if (status === 409 && field === "username")
        return new ApiError({
          status,
          serverText: text,
          field,
          signup: "name_taken",
          fallback: "name taken",
        });
      if (status === 409 && field === "email")
        return new ApiError({
          status,
          serverText: text,
          field,
          signup: "email_taken",
          fallback: "email taken",
        });
      return codeClassify(status, text, field);
    },
  });
}

/** `POST /v1/auth/code/resend` — sends the code that was already issued, never
 * a new one. Inside the ticket's cooldown it sends nothing and says when it
 * will, which from here is the same answer: wait and look in the mailbox. */
export function resendCode(ticket: string, lang: string): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/auth/code/resend",
    body: { ticket, lang },
    bare: true,
    classify: codeClassify,
  });
}

/** `POST /v1/auth/password/forgot` — `login` is an address or a username; the
 * answer is the same for one with an account and one without, so there is
 * nothing here to branch on. */
export function passwordForgot(login: string, lang: string): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/auth/password/forgot",
    body: { login, lang },
    bare: true,
    classify: codeClassify,
  });
}

/** `POST /v1/auth/password/verify` — spends the code for a grant the next
 * screen holds while the person types a new password twice. */
export function passwordVerify(
  ticket: string,
  code: string,
): Promise<{ reset_token: string; expires_in_s: number }> {
  return call({
    method: "POST",
    path: "/v1/auth/password/verify",
    body: { ticket, code },
    bare: true,
    classify: codeClassify,
  });
}

/** `POST /v1/auth/password/reset` — 204, and every session the account had is
 * gone with the old password. The next screen is the login card. */
export function passwordReset(resetToken: string, password: string): Promise<void> {
  return call({
    method: "POST",
    path: "/v1/auth/password/reset",
    body: { reset_token: resetToken, password },
    bare: true,
    classify: codeClassify,
  });
}

/** The 409 an address that is somebody else's answers, on either step of
 * proving one for a signed-in account. */
const addressClassify = (
  status: number,
  text: string | null,
  field: string | null,
): ApiError | null => {
  if (status === 409 && field === "email")
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "email_taken",
      fallback: "email taken",
    });
  // docs/signins.md D499: the account's password is asked for first, and a
  // wrong one is a mistake on the form — never the "401 ⇒ logout" path (R50).
  if (status === 401 && field === "current_password")
    return new ApiError({
      status,
      serverText: text,
      field,
      profile: "wrong_current_password",
      fallback: "wrong password",
    });
  return codeClassify(status, text, field);
};

/** `POST /v1/me/email/start` (D311) — mails a code to the address an account
 * signed in without a proven one wants to prove. Nothing is written until
 * {@link addressConfirm}. The account's password goes with it (docs/signins.md
 * D499): with the session alone, a stolen token could bind its holder's
 * mailbox. The auth deadline, not the control one: the route waits for the
 * relay, the same as `register/start`. */
export function addressStart(
  email: string,
  currentPassword: string,
  lang: string,
): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/me/email/start",
    body: { email, current_password: currentPassword, lang },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: addressClassify,
  });
}

/** `POST /v1/me/email/confirm` — writes the address and the proof together,
 * and answers the account as `GET /v1/me` would, with the gate lifted. */
export function addressConfirm(ticket: string, code: string): Promise<UserJson> {
  return call({
    method: "POST",
    path: "/v1/me/email/confirm",
    body: { ticket, code },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: addressClassify,
  });
}

// Changing a proven address (D312): a code to the address on record, then one
// to the new address. Every step but the first read waits for the relay, so
// all of them take the auth deadline.

/** The address on record, masked by the gateway (`s***y@mail.ru`) — the one
 * shape it is ever shown in. `null` for an account that has none. */
export interface EmailOnRecord {
  email: string | null;
}

/** The grant the old mailbox's code buys: ten minutes to choose the new
 * address, however many letters that takes. */
export interface EmailChangeGrant {
  change_token: string;
  expires_in_s: number;
}

const changeClassify = (
  status: number,
  text: string | null,
  field: string | null,
): ApiError | null => {
  if (status === 400 && field === "change_token")
    return new ApiError({
      status,
      serverText: text,
      field,
      signup: "change_expired",
      fallback: "the e-mail change has expired",
    });
  return addressClassify(status, text, field);
};

/** `GET /v1/me/email` — 501 on a stand without a relay, which is also what
 * every step after it would answer there. */
export function fetchEmailOnRecord(): Promise<EmailOnRecord> {
  return call({ method: "GET", path: "/v1/me/email", classify: codeClassify });
}

/** `POST /v1/me/email/change/start` — a code to the address on record. Asking
 * again is this call again: the same ticket, and nothing mailed inside the
 * minute. */
export function emailChangeStart(lang: string): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/me/email/change/start",
    body: { lang },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: changeClassify,
  });
}

/** `POST /v1/me/email/change/verify` — spends that code for the grant. */
export function emailChangeVerify(ticket: string, code: string): Promise<EmailChangeGrant> {
  return call({
    method: "POST",
    path: "/v1/me/email/change/verify",
    body: { ticket, code },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: changeClassify,
  });
}

/** `POST /v1/me/email/change/new` — a code to the new address. A taken
 * address, and the one already on record, are `email_taken` before anything
 * is mailed. */
export function emailChangeNew(
  changeToken: string,
  email: string,
  lang: string,
): Promise<CodeSent> {
  return call({
    method: "POST",
    path: "/v1/me/email/change/new",
    body: { change_token: changeToken, email, lang },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: changeClassify,
  });
}

/** `POST /v1/me/email/change/confirm` — writes the new address and answers it
 * masked. */
export function emailChangeConfirm(ticket: string, code: string): Promise<EmailOnRecord> {
  return call({
    method: "POST",
    path: "/v1/me/email/change/confirm",
    body: { ticket, code },
    timeoutMs: AUTH_TIMEOUT_MS,
    classify: changeClassify,
  });
}

// ---------------------------------------------------------------------------
// What a start asks (docs/fast-start.md D1053–D1060, chat-api.md §2.1a), the
// people an answer did not name, the account

/** `GET /v1/bootstrap` (D1053): the first answer of every sync — the account,
 * the first `limit` rows less those `known` says this client holds as they are
 * (D1054), whoever those rows name, and the whole list by digest. */
export function fetchBootstrap(
  limit: number,
  known: ReadonlyArray<readonly [channelId: number, digest: number]>,
): Promise<BootstrapResponse> {
  const cursor = known.map(([channelId, digest]) => `${channelId}.${digest}`).join(",");
  return call({
    method: "GET",
    path: `/v1/bootstrap?limit=${limit}${cursor === "" ? "" : `&known=${cursor}`}`,
  });
}

/** `GET /v1/chats/rows` (D1059): the rows `chats` said this client lacks, at most
 * 50 to a request, behind the first screen. A chat the caller may not see is
 * absent from the answer, as one that does not exist is (D114). */
export function fetchChatRows(ids: readonly number[]): Promise<ChatRowsResponse> {
  return call({ method: "GET", path: `/v1/chats/rows?ids=${ids.join(",")}`, background: true });
}

/** `GET /v1/chats/latest` (D1060): the newest `limit` messages of these chats, at
 * most 50 of them, in one request behind the first screen. Each ask is a bare
 * id: the web keeps no revisions, so it never asks for `changes`. */
export function fetchLatest(asks: readonly number[], limit: number): Promise<LatestResponse> {
  return call({
    method: "GET",
    path: `/v1/chats/latest?chats=${asks.join(",")}&limit=${limit}`,
    background: true,
  });
}

/** `GET /v1/users/lookup` (D1057): whoever these ids and handles name, at most
 * 200 and 20. One that names nobody is simply not answered for. */
export function lookupPeople(
  ids: readonly number[],
  handles: readonly string[],
): Promise<DirectoryEntry[]> {
  const query = [
    ...(ids.length > 0 ? [`ids=${ids.join(",")}`] : []),
    ...(handles.length > 0 ? [`handles=${handles.map(encodeURIComponent).join(",")}`] : []),
  ].join("&");
  return call({ method: "GET", path: `/v1/users/lookup?${query}` });
}

/** `GET /v1/users/search` (D1058): the accounts whose handle or display name holds
 * `query`. Too short or too long to mean anybody is an empty answer. */
export function searchPeople(query: string): Promise<DirectoryEntry[]> {
  return call({ method: "GET", path: `/v1/users/search?q=${encodeURIComponent(query)}` });
}

/** `GET /v1/pets/live` (D1058): every pet that is not retired, for the Pets tab
 * (D405). */
export function fetchLivePets(): Promise<DirectoryEntry[]> {
  return call({ method: "GET", path: "/v1/pets/live" });
}

export function fetchMe(): Promise<UserJson> {
  return call({ method: "GET", path: "/v1/me" });
}

/** `PATCH /v1/me` (analytics-plan.md D437): the account switches that are
 * neither profile nor status. A field left out stays as it was; the answer
 * is the account with the switches filled in. */
export function patchMe(patch: MePatch): Promise<UserJson> {
  return call({ method: "PATCH", path: "/v1/me", body: patch });
}

export function fetchChannels(): Promise<ChannelSummary[]> {
  return call({ method: "GET", path: "/v1/channels" });
}

const profileClassify = (
  status: number,
  text: string | null,
  field: string | null,
): ApiError | null => {
  if (status === 409 && field === "username")
    return new ApiError({
      status,
      serverText: text,
      field,
      profile: "username_taken",
      fallback: "username taken",
    });
  // R50: a wrong *current* password is a mistake about the form, not a bad
  // session — it must never reach the "401 ⇒ logout" path.
  if (status === 401 && field === "current_password")
    return new ApiError({
      status,
      serverText: text,
      field,
      profile: "wrong_current_password",
      fallback: "wrong password",
    });
  return null;
};

export function updateProfile(body: {
  username: string;
  display_name: string;
  bio: string;
}): Promise<UserJson> {
  return call({
    method: "PATCH",
    path: "/v1/me/profile",
    body,
    classify: profileClassify,
  });
}

export function setFriendPolicy(policy: string): Promise<UserJson> {
  return call({
    method: "PATCH",
    path: "/v1/me/profile",
    body: { friend_policy: policy },
  });
}

/** `null` (auto) is sent as the string `"auto"`, never as an absent field. */
export function setStatus(status: "auto" | "dnd" | "invisible"): Promise<UserJson> {
  return call({ method: "PUT", path: "/v1/me/status", body: { status } });
}

export function setAvatar(fileId: string): Promise<UserJson> {
  return call({
    method: "PUT",
    path: "/v1/me/avatar",
    body: { file_id: fileId },
  });
}

export function deleteAvatar(): Promise<UserJson> {
  return call({ method: "DELETE", path: "/v1/me/avatar" });
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ jwt: string }> {
  return call({
    method: "POST",
    path: "/v1/me/password",
    body: { current_password: currentPassword, new_password: newPassword },
    classify: profileClassify,
  });
}

// ---------------------------------------------------------------------------
// Channels

export function createChannel(name: string): Promise<{ channel_id: number }> {
  return call({ method: "POST", path: "/v1/channels", body: { name } });
}

/**
 * A group (D274) — its own path and not a `kind` on `/v1/channels`: a gateway
 * older than groups ignores a field it does not know and would make a *public*
 * channel, where an unknown path answers 404 and makes nothing.
 */
export function createGroup(name: string): Promise<{ channel_id: number }> {
  return call({ method: "POST", path: "/v1/groups", body: { name } });
}

/**
 * A broadcast channel (docs/broadcast-channels-plan.md D836, D870) — a route
 * of its own, for D274's reason: a gateway older than the kind answers 404 and
 * makes nothing, where a `kind` it does not know on another route would be
 * ignored. A gateway with creation off answers 403 with its sentence (D862).
 */
export function createBroadcast(body: NewBroadcast): Promise<{ channel_id: number }> {
  return call({ method: "POST", path: "/v1/broadcasts", body });
}

/** The public broadcast channels (D858, D869, D871): those whose name — or
 * handle — holds `q`, the most popular first and an official one on top; for
 * `handle`, the one channel it names, or nothing. Never a private one. */
export function fetchBroadcasts(
  q: { q?: string; handle?: string; limit?: number } = {},
): Promise<BroadcastsResponse> {
  const params = new URLSearchParams();
  if (q.handle !== undefined) params.set("handle", q.handle);
  else params.set("q", q.q ?? "");
  if (q.limit !== undefined) params.set("limit", String(q.limit));
  return call({ method: "GET", path: `/v1/broadcasts?${params.toString()}` });
}

export function deleteChannel(channelId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/channels/${channelId}` });
}

export function updateChannel(
  channelId: number,
  name: string,
  description: string,
): Promise<ChannelSummary> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}`,
    body: { name, description },
  });
}

export function patchChannelSettings(
  channelId: number,
  patch: Record<string, unknown>,
): Promise<ChannelSettings> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}/settings`,
    body: patch,
  });
}

/** 404 is "no link", a state and not a failure — the caller reads
 * `error.notFound`. */
export function fetchInvite(channelId: number): Promise<Invite> {
  return call({ method: "GET", path: `/v1/channels/${channelId}/invite` });
}

export function createInvite(channelId: number): Promise<Invite> {
  return call({ method: "POST", path: `/v1/channels/${channelId}/invite` });
}

export function revokeInvite(channelId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/channels/${channelId}/invite` });
}

// ---------------------------------------------------------------------------
// Membership. The invite token is the one key the door takes (D115); the
// password that used to be the other went with D295.

export function joinChannel(channelId: number, opts: { token?: string } = {}): Promise<Joined> {
  const body = opts.token !== undefined ? opts : undefined;
  return call({ method: "POST", path: `/v1/channels/${channelId}/join`, body });
}

export function leaveChannel(channelId: number): Promise<void> {
  return call({ method: "POST", path: `/v1/channels/${channelId}/leave` });
}

export function fetchMembers(channelId: number): Promise<MembersResponse> {
  return call({ method: "GET", path: `/v1/channels/${channelId}/members` });
}

export function removeMember(channelId: number, userId: number): Promise<void> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/members/${userId}`,
  });
}

export function setMemberAdmin(channelId: number, userId: number, admin: boolean): Promise<void> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}/members/${userId}`,
    body: { admin },
  });
}

export function fetchBans(channelId: number): Promise<BansResponse> {
  return call({ method: "GET", path: `/v1/channels/${channelId}/bans` });
}

/** `untilMs` absent = forever; a past deadline is a 400. Strips membership
 * and ejects from a live call (D124). */
export function banMember(channelId: number, userId: number, untilMs?: number): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/bans`,
    body: untilMs === undefined ? { user_id: userId } : { user_id: userId, until_ms: untilMs },
  });
}

/** Idempotent: 204 whether or not a ban existed. */
export function unbanMember(channelId: number, userId: number): Promise<void> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/bans/${userId}`,
  });
}

/** The persistent voice-mute (D125): written on the membership, applied to a
 * live call if one runs, re-applied on every rejoin. */
export function voiceMuteMember(
  channelId: number,
  userId: number,
  untilMs?: number,
): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/voice-mute`,
    body: untilMs === undefined ? { user_id: userId } : { user_id: userId, until_ms: untilMs },
  });
}

export function voiceUnmuteMember(channelId: number, userId: number): Promise<void> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/voice-mute/${userId}`,
  });
}

/** The call-scoped server mute — a boolean on the live call, dies with it.
 * 404 when the target is not on the call right now. */
export function callMute(channelId: number, userId: number, muted: boolean): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/mute`,
    body: { user_id: userId, muted },
  });
}

/** D112: the target must already be a member; already-owner is a 204 no-op. */
export function transferOwner(channelId: number, userId: number): Promise<void> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}/owner`,
    body: { user_id: userId },
  });
}

/** `null` for a theme = that half has no picture; both halves ride one PUT
 * so the pair changes atomically (D168's fallback reads the other half). */
export function setChannelBackground(
  channelId: number,
  lightFileId: string | null,
  darkFileId: string | null,
): Promise<ChannelSettings> {
  return call({
    method: "PUT",
    path: `/v1/channels/${channelId}/background`,
    body: { light_file_id: lightFileId, dark_file_id: darkFileId },
  });
}

export function clearChannelBackground(channelId: number): Promise<ChannelSettings> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/background`,
  });
}

// ---------------------------------------------------------------------------
// Admin (server-wide): gated by `is_admin`, 403 "admin role required"
// otherwise. The shapes are admin-only and deliberately not part of the
// client dto contract — declared here, where they are consumed.

export interface AdminReport {
  id: number;
  channel_id: number;
  seq: number;
  reporter_id: number | null;
  author_id: number | null;
  reason: string;
  comment: string;
  quoted_text: string;
  created_at_ms: number;
  resolved_at_ms: number | null;
  resolved_by: number | null;
  resolution: string | null;
}

export function fetchAdminReports(all: boolean): Promise<{ open: number; reports: AdminReport[] }> {
  return call({ method: "GET", path: `/v1/admin/reports?all=${all}&limit=100` });
}

export function resolveReport(id: number, resolution: "removed" | "dismissed"): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/admin/reports/${id}/resolve`,
    body: { resolution },
  });
}

// ---------------------------------------------------------------------------
// Messages

export interface PageQuery {
  before?: number;
  after?: number;
  /** The page with that `seq` in its middle (D270): where a link to a post
   * opens its channel (docs/broadcast-channels-plan.md D859). */
  around?: number;
  limit?: number;
}

/** `before`, `after` and `around` are mutually exclusive, in that order of
 * precedence; none means "the newest `limit`". Always ascending by `seq`. */
export function fetchMessages(channelId: number, q: PageQuery = {}): Promise<PageResponse> {
  const limit = Math.min(Math.max(q.limit ?? 50, 1), 100);
  let qs = `?limit=${limit}`;
  if (q.before !== undefined) qs += `&before=${q.before}`;
  else if (q.after !== undefined) qs += `&after=${q.after}`;
  else if (q.around !== undefined) qs += `&around=${q.around}`;
  return call({
    method: "GET",
    path: `/v1/channels/${channelId}/messages${qs}`,
  });
}

export function sendMessage(channelId: number, body: SendBody): Promise<Message> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/messages`,
    body,
  });
}

export function editMessage(channelId: number, seq: number, body: EditBody): Promise<Message> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}/messages/${seq}`,
    body,
  });
}

/** A post's two switches (docs/broadcast-channels-plan.md D851): comments and
 * reactions on or off under it, each absent when it is not moved. Answers the
 * post as it now stands. */
export function setPostOptions(
  channelId: number,
  seq: number,
  body: PostOptionsBody,
): Promise<Message> {
  return call({
    method: "PATCH",
    path: `/v1/channels/${channelId}/messages/${seq}/options`,
    body,
  });
}

/** These posts have been on the reader's screen
 * (docs/broadcast-channels-plan.md D1081): the gateway counts each person once,
 * and answers how many people have seen each of them. */
export function viewPosts(channelId: number, seqs: number[]): Promise<ViewsResponse> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/views`,
    body: { seqs },
  });
}

/** For everybody in the chat, which the reader chose (chat-plan.md D407): a
 * message that may no longer go for everybody is refused with the gateway's
 * sentence rather than narrowed to the reader's view. A gateway older than
 * the choice ignores the query and decides as it always did (D410). */
export function deleteMessage(channelId: number, seq: number): Promise<void> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/messages/${seq}?for=everybody`,
  });
}

/** For the reader alone (chat-plan.md D407): everybody else keeps it. A
 * gateway older than the route answers a bare 404 (`isMissingRoute`). */
export function hideMessage(channelId: number, seq: number): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/messages/${seq}/hide`,
  });
}

/** A selection deleted for everybody in one request (docs/message-selection.md
 * D380, chat-plan.md D407): refused whole or done whole. A gateway older than
 * the route answers 405 — the `{seq}` route's verdict on a POST. */
export function deleteMessages(channelId: number, seqs: number[]): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/messages/delete`,
    body: { seqs, for: "everybody" },
  });
}

/** A selection deleted for the reader alone (chat-plan.md D407). A gateway
 * older than the route answers 405, as it does `deleteMessages`. */
export function hideMessages(channelId: number, seqs: number[]): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/messages/hide`,
    body: { seqs },
  });
}

/** A 404 or a 405 without a sentence of the gateway's: the route itself is
 * not there — a gateway older than it (chat-api.md §7) — rather than the
 * gateway saying no about the thing asked for. How the routes new in
 * chat-plan.md D407 tell an old gateway from a refusal. */
export function isMissingRoute(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 405) && e.serverText === null;
}

/** Answers the **actual** mark after the write (chat-api.md §2.5) — that is
 * the one to keep; a kept optimistic value can pin `unread` at zero forever. */
export function markRead(channelId: number, seq: number): Promise<{ last_read_seq: number }> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/read`,
    body: { seq },
  });
}

export function reportMessage(
  channelId: number,
  seq: number,
  reason: string,
  comment: string,
): Promise<void> {
  return call({
    method: "POST",
    path: "/v1/reports",
    body: { channel_id: channelId, seq, reason, comment },
  });
}

// ---------------------------------------------------------------------------
// Reactions (chat-api.md §2.2a): two verbs, never a toggle.

export function setReaction(
  channelId: number,
  seq: number,
  emoji: string,
): Promise<ReactionsAnswer> {
  return call({
    method: "PUT",
    path: `/v1/channels/${channelId}/messages/${seq}/reaction`,
    body: { emoji },
  });
}

export function clearReaction(channelId: number, seq: number): Promise<ReactionsAnswer> {
  return call({
    method: "DELETE",
    path: `/v1/channels/${channelId}/messages/${seq}/reaction`,
  });
}

// ---------------------------------------------------------------------------
// Friends (chat-api.md §2.0). A request into a block answers the same 204 a
// delivered one gets — the two are the same bytes on purpose (D172), and
// nothing client-side may try to tell them apart.

export function sendFriendRequest(userId: number): Promise<void> {
  return call({
    method: "POST",
    path: "/v1/friends/requests",
    body: { user_id: userId },
  });
}

export function cancelFriendRequest(userId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/friends/requests/${userId}` });
}

export function acceptFriendRequest(userId: number): Promise<void> {
  return call({ method: "POST", path: `/v1/friends/requests/${userId}/accept` });
}

export function declineFriendRequest(userId: number): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/friends/requests/${userId}/decline`,
  });
}

export function unfriend(userId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/friends/${userId}` });
}

/** «Delete chat» on a former friend's conversation (friends-plan.md D412): out
 * of the caller's list, and off the server when the other side has deleted
 * theirs already. */
export function forgetConversation(channelId: number): Promise<void> {
  return call({ method: "POST", path: `/v1/channels/${channelId}/forget` });
}

/** «Delete chat» on a live dialog, for the caller alone (friends-plan.md
 * D465): everything up to `seq`, while the other side keeps all of it.
 * Answers where the mark stands — higher than `seq` when another of the
 * caller's devices got there first. A gateway older than the route answers a
 * bare 404 (`isMissingRoute`). */
export function clearHistory(channelId: number, seq: number): Promise<{ cleared_seq: number }> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/clear`,
    body: { seq },
  });
}

export function blockUser(userId: number): Promise<void> {
  return call({ method: "PUT", path: `/v1/blocks/${userId}` });
}

export function unblockUser(userId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/blocks/${userId}` });
}

/** Fetched fresh every time a card opens — and the card shows a placeholder
 * while this flies, never defaults (web-plan.md F3). */
/** `GET /v1/channels/{id}/messages/{seq}/reads` (chat-plan.md D422): who
 * other than the caller has read their own message `seq`, and the minute each
 * did, newest reader first — what «Details» in its menu draws (D423). 403 for
 * anybody else's message; a gateway older than the route answers 404. */
export function fetchMessageReads(channelId: number, seq: number): Promise<MessageReads> {
  return call({ method: "GET", path: `/v1/channels/${channelId}/messages/${seq}/reads` });
}

export function fetchFriendship(userId: number): Promise<Friendship> {
  return call({ method: "GET", path: `/v1/users/${userId}/friendship` });
}

export function declineCall(channelId: number): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/call/decline`,
  });
}

/** The answer to the after-call question (docs/call-rating.md D517): one
 * word. What the call was like is the gateway's own record of it. */
export function rateCall(channelId: number, rating: "good" | "bad"): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/call/rating`,
    body: { rating },
  });
}

/** Ends one of the account's sign-ins (docs/signins.md D504) — the web's first
 * caller of the route, from a security notice's «End session»
 * (docs/official-pet-plan.md D942). 204 is done, and so is 404: the session
 * had ended already, by another device or by itself. */
export async function endSignin(id: string): Promise<void> {
  try {
    await call<void>({ method: "DELETE", path: `/v1/me/signins/${encodeURIComponent(id)}` });
  } catch (e) {
    if (e instanceof ApiError && e.notFound) return;
    throw e;
  }
}

/** `POST /v1/feedback` (D231, official-pet-plan.md D947): what the person
 * typed, which may be nothing, and what the page can say about itself. 204;
 * the gateway clips both halves and keeps its own hourly budget (429). */
export function sendFeedback(text: string, report: string): Promise<void> {
  return call({ method: "POST", path: "/v1/feedback", body: { text, report } });
}

// ---------------------------------------------------------------------------
// Pets (chat-api.md §2.0c, pets-plan.md D349–D368). Every route that changes a
// pet answers its whole card. The token rides the answers of `createPet` and
// `rotatePetToken` and no other, ever (D357). A refusal's `field` names what
// it is about — `username`, `description` or `commands` — which the form
// puts its sentence under.

/** `GET /v1/pets/{id}` — anybody signed in, a retired pet included. */
export function fetchPet(petId: number): Promise<PetCard> {
  return call({ method: "GET", path: `/v1/pets/${petId}` });
}

/** `POST /v1/pets` — `[pets] creation` decides who may (D363); a 404 is a
 * gateway older than pets, which made nothing. */
export function createPet(draft: PetDraft): Promise<PetCreated> {
  return call({ method: "POST", path: "/v1/pets", body: draft });
}

/** The name, the description (the whole map) and `joinable` — the owner's and
 * a server admin's, like every write below. */
export function patchPet(petId: number, patch: PetPatch): Promise<PetCard> {
  return call({ method: "PATCH", path: `/v1/pets/${petId}`, body: patch });
}

/** The list whole, in display order (D353): no per-command verbs. */
export function putPetCommands(petId: number, commands: PetCommand[]): Promise<PetCard> {
  return call({ method: "PUT", path: `/v1/pets/${petId}/commands`, body: { commands } });
}

/** `PUT /v1/me/avatar`'s route for the pet (D352): an uploaded photo's id. */
export function setPetAvatar(petId: number, fileId: string): Promise<PetCard> {
  return call({ method: "PUT", path: `/v1/pets/${petId}/avatar`, body: { file_id: fileId } });
}

export function deletePetAvatar(petId: number): Promise<PetCard> {
  return call({ method: "DELETE", path: `/v1/pets/${petId}/avatar` });
}

/** The old token is dead the moment this answers. */
export function rotatePetToken(petId: number): Promise<PetToken> {
  return call({ method: "POST", path: `/v1/pets/${petId}/token` });
}

/** Retires the pet (D365): idempotent, and there is no way back. */
export function retirePet(petId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/pets/${petId}` });
}

/** The dialog with a pet (D358): made the first time, the same one after. */
export function openPetDm(petId: number): Promise<PetDm> {
  return call({ method: "POST", path: `/v1/pets/${petId}/dm` });
}

/** Stop (D368). Taking it back is no route: it is the message `/restart`. */
export function stopPet(petId: number): Promise<void> {
  return call({ method: "POST", path: `/v1/pets/${petId}/stop` });
}

/** A group's own admins put a pet into it (D359). */
export function addPetToGroup(channelId: number, petId: number): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/channels/${channelId}/pets`,
    body: { pet_id: petId },
  });
}

/** Every command of every live pet in the chat (D360), each pet's in its own
 * display order — what the composer's `/` list offers. */
export function fetchCommands(channelId: number): Promise<ChatCommand[]> {
  return call({ method: "GET", path: `/v1/channels/${channelId}/commands` });
}

// ---------------------------------------------------------------------------
// Sticker packs (docs/stickers-plan.md D546–D566, chat-api.md §2.11) — the
// desktop's `net/rest.rs` twins. A gateway older than the routes answers 404 to
// each, which is how the composer learns to hide its sticker door (D560).

/** A page of the catalogue (D566): published packs, each with its first
 * stickers; `cursor` is the gateway's, opaque. */
export function fetchStickerCatalog(
  query: string,
  order: "popular" | "new",
  cursor: string | null,
  limit = 20,
): Promise<StickerCatalog> {
  const qs = new URLSearchParams({ order, limit: String(limit) });
  if (query !== "") qs.set("q", query);
  if (cursor !== null) qs.set("cursor", cursor);
  return call({ method: "GET", path: `/v1/sticker-packs?${qs.toString()}` });
}

/** A pack whole; 404 for one this reader cannot see. */
export function fetchStickerPack(packId: number): Promise<StickerPack> {
  return call({ method: "GET", path: `/v1/sticker-packs/${packId}` });
}

export function createStickerPack(title: string): Promise<StickerPack> {
  return call({ method: "POST", path: "/v1/sticker-packs", body: { title } });
}

export function renameStickerPack(packId: number, title: string): Promise<StickerPack> {
  return call({ method: "PATCH", path: `/v1/sticker-packs/${packId}`, body: { title } });
}

export function deleteStickerPack(packId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/sticker-packs/${packId}` });
}

export function publishStickerPack(packId: number): Promise<StickerPack> {
  return call({ method: "POST", path: `/v1/sticker-packs/${packId}/publish` });
}

/** The one door a sticker's bytes have (D548): the file and, for a TGS, its
 * poster, in base64, with its emoji — one request. */
export function addSticker(packId: number, sticker: NewSticker): Promise<Sticker> {
  return call({ method: "POST", path: `/v1/sticker-packs/${packId}/stickers`, body: sticker });
}

export function editSticker(packId: number, fileId: string, patch: StickerPatch): Promise<Sticker> {
  return call({
    method: "PATCH",
    path: `/v1/sticker-packs/${packId}/stickers/${fileId}`,
    body: patch,
  });
}

export function deleteSticker(packId: number, fileId: string): Promise<void> {
  return call({ method: "DELETE", path: `/v1/sticker-packs/${packId}/stickers/${fileId}` });
}

/** D558: a reason out of the guidelines' list, and a comment that may be
 * empty. */
export function reportStickerPack(packId: number, reason: string, comment: string): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/sticker-packs/${packId}/report`,
    body: { reason, comment },
  });
}

/** The installed packs, in the account's order, each with its cover (D560). */
export function fetchInstalledStickerPacks(): Promise<StickerPackList> {
  return call({ method: "GET", path: "/v1/me/sticker-packs" });
}

/** The packs the reader made. */
export function fetchOwnStickerPacks(): Promise<StickerPackList> {
  return call({ method: "GET", path: "/v1/me/sticker-packs/own" });
}

/** Past the two hundredth the gateway says so in words (D560). */
export function installStickerPack(packId: number): Promise<void> {
  return call({ method: "PUT", path: `/v1/me/sticker-packs/${packId}` });
}

export function uninstallStickerPack(packId: number): Promise<void> {
  return call({ method: "DELETE", path: `/v1/me/sticker-packs/${packId}` });
}

/** Newest first, at most twenty, only what can still be sent (D561). */
export function fetchStickerRecents(): Promise<StickerRecents> {
  return call({ method: "GET", path: "/v1/me/sticker-recents" });
}

/** What one emoji alone in the composer offers (D574): at most ten stickers
 * that carry it, the caller's own first. A bare 404 is a gateway older than
 * the route. */
export function fetchStickerSuggestions(emoji: string): Promise<StickerSuggestions> {
  return call({
    method: "GET",
    path: `/v1/me/sticker-suggestions?emoji=${encodeURIComponent(emoji)}`,
  });
}

/** A server administrator's take-down (D551) — with its files, everywhere,
 * when `blockFiles`. */
export function removeStickerPack(packId: number, blockFiles: boolean): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/admin/sticker-packs/${packId}/remove`,
    body: { block_files: blockFiles },
  });
}

/** An admin's view of a pack report (D558) — admin-only, declared here where
 * it is consumed, as `AdminReport` is. */
export interface AdminPackReport {
  id: number;
  pack_id: number;
  reporter_id: number | null;
  owner_id: number | null;
  title: string;
  reason: string;
  comment: string;
  created_at_ms: number;
  resolved_at_ms: number | null;
  resolved_by: number | null;
  resolution: string | null;
}

export function fetchAdminPackReports(
  all: boolean,
): Promise<{ open: number; reports: AdminPackReport[] }> {
  return call({ method: "GET", path: `/v1/admin/sticker-pack-reports?all=${all}&limit=100` });
}

export function resolvePackReport(id: number, resolution: "removed" | "dismissed"): Promise<void> {
  return call({
    method: "POST",
    path: `/v1/admin/sticker-pack-reports/${id}/resolve`,
    body: { resolution },
  });
}

// ---------------------------------------------------------------------------
// Link cards (docs/link-previews.md D280, D281)

/** The gateway may spend nine seconds on one preview (the page, then its
 * picture, then a second's slack), which is most of a control deadline; this
 * one leaves room past it for the queue on the way in. */
export const LINK_PREVIEW_TIMEOUT_MS = 12_000;

/** What a page says about itself, read by the gateway and never by this
 * machine (D280). 404 is "no card" — nothing worth one, a refused address,
 * and a gateway older than the route all answer it; 429 is the account's
 * fetch budget and 503 a fetch that outlived this request, both "ask later". */
export function fetchLinkPreview(url: string): Promise<LinkPreview> {
  return call({
    method: "GET",
    path: `/v1/link-preview?url=${encodeURIComponent(url)}`,
    timeoutMs: LINK_PREVIEW_TIMEOUT_MS,
  });
}

/** The card of a channel this reader holds no row of (D281) — with the
 * link's invite token when it carried one, which is what makes a private
 * channel visible. 404 for everything else, never a 403. */
export function fetchChannelCard(channelId: number, token?: string | null): Promise<ChannelCard> {
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return call({ method: "GET", path: `/v1/channels/${channelId}/card${qs}` });
}

// ---------------------------------------------------------------------------
// Blob addresses (D119/D120/D121): the channel is in the address; knowing a
// file_id opens nothing.

/** length 64, lowercase hex — checked at every boundary a file_id crosses. */
export function isFileId(s: string): boolean {
  return /^[0-9a-f]{64}$/.test(s);
}

export function channelFileUrl(channelId: number, fileId: string): string {
  if (!isFileId(fileId)) return "";
  return `/v1/channels/${channelId}/files/${fileId}`;
}

export function avatarUrl(fileId: string): string {
  if (!isFileId(fileId)) return "";
  return `/v1/avatars/${fileId}`;
}

/** A page card's picture (D280): its id is the sha256 of its bytes, the shape
 * of a file_id, and the worker authorizes and caches it like an avatar. */
export function linkImageUrl(id: string): string {
  if (!isFileId(id)) return "";
  return `/v1/link-preview/images/${id}`;
}

/** A sticker's bytes or its poster (docs/stickers-plan.md D551): one route for
 * the feed, the panel and the catalogue, so one cache entry everywhere. */
export function stickerUrl(fileId: string): string {
  if (!isFileId(fileId)) return "";
  return `/v1/stickers/${fileId}`;
}
